import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  VERSION_POLL_MS,
  canReloadOnRequest,
  currentBuildId,
  decideReload,
  fetchDeployedBuildId,
  hasUnsavedWork,
  isDialogOpen,
  isKitchenPath,
} from "@/lib/versionCheck";

/** While a new build waits for an idle moment, check this often. */
const RECHECK_MS = 5_000;
const INPUT_EVENTS = ["pointerdown", "keydown", "touchstart", "wheel"] as const;
// Stable defaults (props default to these, so effects don't re-run each render).
const reloadPage = () => window.location.reload();
const readDeployed = () => fetchDeployedBuildId();

interface VersionWatcherProps {
  /** The running build (tests pass one; dev has none, so nothing runs). */
  buildId?: string | null;
  /** Reload the page (tests pass a spy). */
  reload?: () => void;
  /** Read the deployed build (tests pass a stub). */
  fetchDeployed?: () => Promise<string | null>;
}

/**
 * Reloads a tab left open across a deploy: right away when the user is idle
 * (and nothing is open or unsaved), else after showing a small banner and at
 * the next idle moment. The kitchen display reloads when idle without asking.
 * Never while a mutation is in flight; a missing /version.json does nothing.
 */
export function VersionWatcher({
  buildId = currentBuildId(),
  reload = reloadPage,
  fetchDeployed = readDeployed,
}: VersionWatcherProps) {
  const queryClient = useQueryClient();
  const { pathname } = useLocation();
  const [deployed, setDeployed] = useState<string | null>(null);
  const [showBanner, setShowBanner] = useState(false);
  const lastInput = useRef(Date.now());
  const reloading = useRef(false);
  const pathRef = useRef(pathname);
  pathRef.current = pathname;

  const doReload = useCallback(() => {
    if (reloading.current) return;
    reloading.current = true;
    reload();
  }, [reload]);

  // Decide with the latest state (called from timers and events).
  const evaluate = useCallback(
    (deployedId: string | null) => {
      const decision = decideReload({
        current: buildId,
        deployed: deployedId,
        idleMs: Date.now() - lastInput.current,
        dialogOpen: isDialogOpen(),
        unsavedWork: hasUnsavedWork(),
        mutating: queryClient.isMutating() > 0,
        isKitchen: isKitchenPath(pathRef.current),
      });
      if (decision === "reload") doReload();
      else setShowBanner(decision === "prompt");
    },
    [buildId, doReload, queryClient],
  );

  // Track input for idleness.
  useEffect(() => {
    if (!buildId) return;
    const onInput = () => {
      lastInput.current = Date.now();
    };
    for (const type of INPUT_EVENTS) window.addEventListener(type, onInput, { passive: true, capture: true });
    return () => {
      for (const type of INPUT_EVENTS) window.removeEventListener(type, onInput, { capture: true });
    };
  }, [buildId]);

  // Poll /version.json: every couple of minutes, on focus and when the tab is shown.
  useEffect(() => {
    if (!buildId) return;
    let cancelled = false;
    const check = async () => {
      const id = await fetchDeployed();
      if (cancelled || !id) return;
      setDeployed(id);
    };
    void check();
    const timer = window.setInterval(() => void check(), VERSION_POLL_MS);
    const onFocus = () => void check();
    const onVisible = () => {
      if (document.visibilityState === "visible") void check();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [buildId, fetchDeployed]);

  // A new build is out: decide now, then keep checking for an idle moment.
  useEffect(() => {
    if (!buildId || !deployed || deployed === buildId) return;
    evaluate(deployed);
    const timer = window.setInterval(() => evaluate(deployed), RECHECK_MS);
    return () => window.clearInterval(timer);
  }, [buildId, deployed, evaluate]);

  if (!showBanner) return null;
  return (
    <div
      role="status"
      data-version-banner
      className="fixed bottom-3 left-1/2 z-[70] flex -translate-x-1/2 items-center gap-3 rounded-full border border-line bg-card px-4 py-2 text-sm text-ink shadow-md"
    >
      <span>A new version is available</span>
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="h-8 gap-1.5 rounded-full"
        onClick={() => {
          if (canReloadOnRequest({ mutating: queryClient.isMutating() > 0 })) doReload();
        }}
      >
        <RefreshCw className="h-3.5 w-3.5" />
        Reload
      </Button>
    </div>
  );
}
